import React, { useEffect, useMemo, useState } from 'react';
import Header from '../Components/Header';
import Banner from '../Components/Banner';
import Collections from '../Components/Collections';
import Footer from '../Components/Footer';
import { products } from '../data';

const CART_STORAGE_KEY = 'unique-mart-cart';

const MainPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOption, setSortOption] = useState('default');
  const [cartItems, setCartItems] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [toastMessage, setToastMessage] = useState('');

  useEffect(() => {
    const savedCart = localStorage.getItem(CART_STORAGE_KEY);
    if (savedCart) {
      setCartItems(JSON.parse(savedCart));
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartItems));
  }, [cartItems]);

  useEffect(() => {
    if (!toastMessage) {
      return undefined;
    }

    const timer = setTimeout(() => setToastMessage(''), 2200);
    return () => clearTimeout(timer);
  }, [toastMessage]);

  const filteredProducts = useMemo(() => {
    let items = [...products];

    if (selectedCategory !== 'All') {
      items = items.filter((product) => product.category === selectedCategory);
    }

    if (searchTerm.trim()) {
      const query = searchTerm.toLowerCase();
      items = items.filter(
        (product) =>
          product.name.toLowerCase().includes(query) ||
          product.category.toLowerCase().includes(query) ||
          product.badge.toLowerCase().includes(query)
      );
    }

    if (sortOption === 'price-low-high') {
      items.sort((a, b) => a.price - b.price);
    } else if (sortOption === 'price-high-low') {
      items.sort((a, b) => b.price - a.price);
    } else if (sortOption === 'rating-high-low') {
      items.sort((a, b) => b.rating - a.rating);
    }

    return items;
  }, [searchTerm, selectedCategory, sortOption]);

  const cartCount = useMemo(
    () => cartItems.reduce((total, item) => total + item.quantity, 0),
    [cartItems]
  );

  const cartSubtotal = useMemo(
    () => cartItems.reduce((total, item) => total + item.price * item.quantity, 0),
    [cartItems]
  );

  const showToast = (message) => {
    setToastMessage(message);
  };

  const handleAddToCart = (product) => {
    setCartItems((currentItems) => {
      const existingItem = currentItems.find((item) => item.id === product.id);

      if (existingItem) {
        return currentItems.map((item) =>
          item.id === product.id ? { ...item, quantity: item.quantity + 1 } : item
        );
      }

      return [...currentItems, { ...product, quantity: 1 }];
    });

    showToast(`${product.name} added to cart`);
  };

  const handleIncreaseQuantity = (productId) => {
    setCartItems((currentItems) =>
      currentItems.map((item) =>
        item.id === productId ? { ...item, quantity: item.quantity + 1 } : item
      )
    );
  };

  const handleDecreaseQuantity = (productId) => {
    setCartItems((currentItems) =>
      currentItems
        .map((item) =>
          item.id === productId ? { ...item, quantity: item.quantity - 1 } : item
        )
        .filter((item) => item.quantity > 0)
    );
  };

  const handleRemoveItem = (productId) => {
    setCartItems((currentItems) => currentItems.filter((item) => item.id !== productId));
    showToast('Item removed from cart');
  };

  return (
    <div className="appShell">
      <Header
        selectedCategory={selectedCategory}
        onCategoryChange={setSelectedCategory}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        cartCount={cartCount}
        onCartToggle={() => setIsCartOpen((value) => !value)}
      />

      <main className="mainContent">
        <Banner />

        <Collections
          products={filteredProducts}
          selectedCategory={selectedCategory}
          sortOption={sortOption}
          onSortChange={setSortOption}
          onCategoryChange={setSelectedCategory}
          onAddToCart={handleAddToCart}
          onViewDetails={setSelectedProduct}
        />

        {filteredProducts.length === 0 ? (
          <section className="emptyStateCard">
            <h3>No products found</h3>
            <p>Try a different keyword or reset the selected category.</p>
            <button
              type="button"
              className="primaryBtn"
              onClick={() => {
                setSearchTerm('');
                setSelectedCategory('All');
                setSortOption('default');
              }}
            >
              Reset Filters
            </button>
          </section>
        ) : null}
      </main>

      <aside className={isCartOpen ? 'cartDrawer open' : 'cartDrawer'}>
        <div className="cartDrawerHeader">
          <div>
            <p className="sectionLabel">Shopping Cart</p>
            <h3>{cartCount} item(s)</h3>
          </div>
          <button type="button" className="iconBtn" onClick={() => setIsCartOpen(false)}>
            ✕
          </button>
        </div>

        {cartItems.length === 0 ? (
          <div className="cartEmptyState">
            <h4>Your cart is empty</h4>
            <p>Add products to demonstrate interactive cart functionality.</p>
          </div>
        ) : (
          <>
            <div className="cartItemsList">
              {cartItems.map((item) => (
                <div className="cartItemCard" key={item.id}>
                  <img src={item.image} alt={item.name} />
                  <div className="cartItemInfo">
                    <h4>{item.name}</h4>
                    <p>{item.category}</p>
                    <strong>₹{item.price}</strong>
                    <div className="quantityControls">
                      <button type="button" onClick={() => handleDecreaseQuantity(item.id)}>
                        -
                      </button>
                      <span>{item.quantity}</span>
                      <button type="button" onClick={() => handleIncreaseQuantity(item.id)}>
                        +
                      </button>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="removeBtn"
                    onClick={() => handleRemoveItem(item.id)}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>

            <div className="cartSummary">
              <div className="summaryRow">
                <span>Subtotal</span>
                <strong>₹{cartSubtotal}</strong>
              </div>
              <div className="summaryRow">
                <span>Shipping</span>
                <strong>Free</strong>
              </div>
              <button type="button" className="primaryBtn checkoutBtn">
                Checkout Demo
              </button>
            </div>
          </>
        )}
      </aside>

      {selectedProduct ? (
        <div className="modalOverlay" onClick={() => setSelectedProduct(null)}>
          <div className="productModal" onClick={(event) => event.stopPropagation()}>
            <button type="button" className="iconBtn modalClose" onClick={() => setSelectedProduct(null)}>
              ✕
            </button>
            <div className="modalGrid">
              <img src={selectedProduct.image} alt={selectedProduct.name} className="modalImage" />
              <div>
                <p className="sectionLabel">Product Details</p>
                <h3>{selectedProduct.name}</h3>
                <div className="productMeta modalMeta">
                  <span className="productCategory">{selectedProduct.category}</span>
                  <span className="productRating">★ {selectedProduct.rating}</span>
                  <span className="productBadge alt">{selectedProduct.badge}</span>
                </div>
                <p className="modalDescription">{selectedProduct.description}</p>
                <strong className="modalPrice">₹{selectedProduct.price}</strong>
                <div className="modalActions">
                  <button type="button" className="secondaryBtn" onClick={() => setSelectedProduct(null)}>
                    Continue Browsing
                  </button>
                  <button
                    type="button"
                    className="primaryBtn"
                    onClick={() => {
                      handleAddToCart(selectedProduct);
                      setSelectedProduct(null);
                    }}
                  >
                    Add to Cart
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {toastMessage ? <div className="toastMessage">{toastMessage}</div> : null}

      <Footer />
    </div>
  );
};

export default MainPage;
